import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  AlertCircle, Hand, User, ClipboardList, Clock,
  Baby, Heart, Shield, Brain, Utensils, ChevronDown, ChevronUp, ChevronsDown, ChevronsUp
} from 'lucide-react';
import { FormPageLayout, DocStatusPill } from '../../common';
import type { ActionButtonConfig } from '../../common';
import { CarePlanStatus } from '../../../services/patientmr/careplans.service';
import { useCarePlanDraft } from './hooks/useCarePlanDraft';
import GoalsEditor from './goals/GoalsEditor';
import { CarePlanGoal, initialGoals, clearedLegacyGoalFields, goalErrors } from './goals/carePlanGoals';
import ICDSelect from '../../common/ICDSelect';
import OrderingPhysicianSection from './sections/OrderingPhysicianSection';
import CertPeriodSection, { applyCertPeriod, hasFixedCertPeriod } from './sections/CertPeriodSection';
import type { CarePlanCertificationPeriod } from '../../../dtos/patientmr/careplans/careplan.dto';
import HomeboundSection, { homeboundJustificationField } from './sections/HomeboundSection';
import MedicalNecessitySection from './sections/MedicalNecessitySection';
import ProviderSection from './sections/ProviderSection';
import type { SectionFieldConfig } from './sections/SectionCard';
import { AssessmentsService, AssessmentListItem, AssessmentDiscipline } from '../../../services/patientmr/assessments.service';

interface OTCarePlanFormProps {
  editingId?: string | null;
  patientId?: string;
  existingData?: any;
  onSave: (data: any, submitAfterSave?: boolean) => void;
  onSubmit?: () => void;
  onReopen?: () => void;
  onApprove?: () => void;
  onCancel: () => void;
  saving?: boolean;
  submitting?: boolean;
  reopening?: boolean;
  carePlanStatus?: CarePlanStatus;
  isReadOnly?: boolean;
  /** Start QA review (SUBMITTED -> QA); shown only when provided */
  onStartQA?: () => void;
  startingQA?: boolean;
  /** Extra header content (version badge, version picker) */
  headerExtra?: React.ReactNode;
  /** Saves a draft without closing the form (autosave) */
  onAutosave?: (data: Record<string, unknown>) => Promise<void>;
  /** Autosave runs only while true */
  autosaveEnabled?: boolean;
  /** Phase 19 F26: the plan record's certification period; its dates are shown read-only */
  certificationPeriod?: CarePlanCertificationPeriod | null;
}

interface CheckboxFieldProps {
  field: string;
  label: string;
  checked: boolean;
  onChange: (field: string, value: boolean) => void;
  isReadOnly?: boolean;
}

const CheckboxField = React.memo(({ field, label, checked, onChange, isReadOnly = false }: CheckboxFieldProps) => (
  <label className={`flex items-center space-x-2 text-sm text-gray-700 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(field, e.target.checked)}
      disabled={isReadOnly}
      className={`rounded border-gray-300 text-purple-600 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
    />
    <span>{label}</span>
  </label>
));

// Phase 19 F25: per-plan copy for the shared sections (keys unchanged).
const OT_HOMEBOUND_STATUSES = [
  'Patient is homebound due to medical condition',
  'Patient requires considerable effort to leave home',
  'Patient leaves home for medical appointments only',
  'Patient requires assistive devices and assistance to leave home',
];

const otHomeboundFields = (isPediatric: boolean): SectionFieldConfig[] => [
  { key: 'homeboundStatus', label: 'Homebound Status', kind: 'select', required: true,
    options: [{ value: '', label: 'Select status' }, ...OT_HOMEBOUND_STATUSES.map(v => ({ value: v, label: v }))] },
  homeboundJustificationField({ rows: 2, wide: false, placeholder: isPediatric
    ? 'e.g., Patient is medically fragile with trach/vent and requires skilled nursing during transport'
    : 'e.g., Patient requires assistance for all ADLs and mobility' }),
];

const OT_MEDICAL_NECESSITY_FIELDS: SectionFieldConfig[] = [
  { key: 'medicalNecessityStatement', label: 'Medical necessity statement', srOnlyLabel: true, kind: 'textarea', rows: 4,
    placeholder: 'Comprehensive medical necessity statement for skilled OT services. Include: diagnosis, functional deficits in ADLs/fine motor/sensory, skilled interventions required, expected outcomes, why services cannot be provided in outpatient setting (for home health)' },
];

const OTCarePlanForm: React.FC<OTCarePlanFormProps> = ({
  editingId,
  patientId,
  existingData,
  onSave,
  onReopen,
  onApprove,
  onCancel,
  saving = false,
  submitting = false,
  reopening = false,
  carePlanStatus = CarePlanStatus.DRAFT,
  isReadOnly = false,
  onStartQA,
  startingQA = false,
  headerExtra,
  onAutosave,
  autosaveEnabled = false,
  certificationPeriod = null
}) => {
  // Form configuration - MVP defaults to pediatric home health
  const [formConfig, setFormConfig] = useState({
    patientType: 'pediatric' as 'pediatric' | 'adult',
    settingType: 'home-health' as 'home-health' | 'outpatient',
    isPDN: true,
  });

  const [formData, setFormData] = useState({
    // Phase 19 F12: every goal of the plan (domain, measure, target date, status, progress)
    goals: [] as CarePlanGoal[],
    // === ASSESSMENT REFERENCE ===
    assessmentId: '',
    assessmentDate: '',
    assessedBy: '',
    assessmentType: '',
    
    // === CERTIFICATION PERIOD (CMS-485 / Texas Medicaid) ===
    certificationPeriodFrom: '',
    certificationPeriodTo: '',

    // === ORDERING PHYSICIAN (Phase 19 F3: signer of the plan of care) ===
    orderingPhysicianName: '',
    orderingPhysicianNPI: '',
    orderingPhysicianPhone: '',
    orderingPhysicianFax: '',
    orderingPhysicianSpecialty: '',
    orderingPhysicianAddressStreet: '',
    orderingPhysicianAddressCity: '',
    orderingPhysicianAddressState: '',
    orderingPhysicianAddressZip: '',
    
    // === DIAGNOSIS INFORMATION ===
    primaryDiagnosis: '',
    primaryDiagnosisICD10: '',
    secondaryDiagnoses: '',
    secondaryDiagnosesICD10: '',
    
    // === CLINICAL STATUS ===
    prognosis: '',
    rehabilitationPotential: '',
    
    // === FUNCTIONAL LIMITATIONS (CMS-485 Section) ===
    functionalLimitationSelfCare: false,
    functionalLimitationUpperExtremity: false,
    functionalLimitationFineMotor: false,
    functionalLimitationSensoryProcessing: false,
    functionalLimitationCognitive: false,
    functionalLimitationVisualPerceptual: false,
    functionalLimitationFeeding: false,
    functionalLimitationWriting: false,
    functionalLimitationOther: '',
    
    // === ACTIVITIES PERMITTED (CMS-485 Section) ===
    activityBedrest: false,
    activityBRP: false,
    activityUpAsTolerated: false,
    activityOtherActivity: '',
    
    // === HOMEBOUND STATUS (Home Health Requirement) ===
    homeboundStatus: '',
    homeboundJustification: '',
    
    // === PEDIATRIC DEVELOPMENTAL FINE MOTOR GOALS ===
    pediatricChronologicalAge: '',
    pediatricAdjustedAge: '',
    pediatricFineMotorAge: '',
    pediatricDevelopmentalMilestones: '',
    pediatricFineMotorGoal1: '',
    pediatricFineMotorGoal2: '',
    pediatricFineMotorGoal3: '',
    pediatricSensoryProcessingGoal: '',
    pediatricSelfCareGoal: '',
    pediatricFeedingGoal: '',
    pediatricVisualMotorGoal: '',
    pediatricPlaySkillsGoal: '',
    
    // === PEDIATRIC PDN - OT CONSIDERATIONS ===
    pdnTracheostomyStatus: '',
    pdnVentilatorStatus: '',
    pdnMedicalStabilityForOT: '',
    pdnOTSessionPrecautions: '',
    pdnPositioningForOT: '',
    pdnFeedingStatus: '',
    
    // === PEDIATRIC PDN - SENSORY PROCESSING ===
    pdnSensoryProfile: '',
    pdnTactileProcessing: '',
    pdnVestibularProcessing: '',
    pdnOralSensoryProcessing: '',
    pdnSensoryDietPlan: '',
    pdnSensoryGoal: '',
    
    // === PEDIATRIC PDN - FEEDING/ORAL MOTOR ===
    pdnCurrentFeedingMethod: '',
    pdnOralFeedingStatus: '',
    pdnOralTrialsPlan: '',
    pdnFeedingPositioning: '',
    pdnSelfFeedingStatus: '',
    pdnAdaptedFeedingEquipment: '',
    pdnFeedingOTGoal: '',
    pdnFeedingOTIntervention: '',
    
    // === PEDIATRIC PDN - CAREGIVER OT TRAINING ===
    pdnOTCaregiverName: '',
    pdnOTCaregiverTrainingStatus: '',
    pdnOTCaregiverCompetencies: '',
    pdnOTCaregiverTrainingPlan: '',
    pdnSensoryDietTraining: '',
    pdnFeedingTechniqueTraining: '',
    
    // === OT GOALS (SMART Goals for Billing) ===
    goalSelfCare: '',
    goalFineMotor: '',
    goalSensoryProcessing: '',
    goalVisualMotor: '',
    goalFeeding: '',
    goalCognitive: '',
    goalUpperExtremityFunction: '',
    goalWritingSkills: '',
    goalPlaySkills: '',
    goalCaregiverEducation: '',
    goalOther: '',
    
    // === OT INTERVENTIONS ===
    interventionTherapeuticActivity: '',
    interventionSensoryIntegration: '',
    interventionFineMotorTraining: '',
    interventionFeedingTherapy: '',
    interventionADLTraining: '',
    interventionCaregiverTraining: '',
    
    // === OT VISIT FREQUENCY AND DURATION ===
    otFrequency: '',
    otFrequencyCustom: '',
    otDuration: '',
    otSessionLength: '',
    otFrequencyJustification: '',
    
    // === DME AND EQUIPMENT ===
    dmeOther: '',
    
    // === SAFETY MEASURES ===
    safetySkinIntegrityPrecautions: false,
    safetyFeedingPrecautions: false,
    safetyAspirationPrecautions: false,
    safetySeizurePrecautions: false,
    safetySensoryOverloadPrecautions: false,
    safetySupervisionRequired: '',
    safetyEmergencyPlanReviewed: false,
    safetyOther: '',
    
    // === COORDINATION OF CARE ===
    coordinationWithNursing: '',
    coordinationWithPT: '',
    coordinationWithST: '',
    coordinationWithSchool: '',
    
    // === DISCHARGE PLANNING ===
    dischargeGoals: '',
    dischargeCriteria: '',
    
    // === PROVIDER INFORMATION ===
    providerName: '',
    providerCredentials: '',
    providerNPI: '',
    supervisingPhysician: '',
    supervisingPhysicianNPI: '',
    dateCreated: new Date().toISOString().split('T')[0],
    
    // === ADDITIONAL NOTES ===
    additionalNotes: '',
    medicalNecessityStatement: ''
  });

  const [assessmentsList, setAssessmentsList] = useState<AssessmentListItem[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const hasLoadedExistingData = useRef(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [collapsedSections, setCollapsedSections] = useState({
    config: false, // expanded by default
    certification: true,
    diagnosis: true,
    orderingPhysician: true,
    clinicalStatus: true,
    homebound: true,
    functionalLimitations: true,
    pediatricDev: true,
    pdnOT: true,
    pdnSensory: true,
    pdnFeeding: true,
    pdnCaregiver: true,
    goals: true,
    interventions: true,
    frequency: true,
    coordination: true,
    provider: true,
    medicalNecessity: true,
    additionalNotes: true
  });
  const allSectionsCollapsed = useMemo(
    () => Object.values(collapsedSections).every(v => v === true),
    [collapsedSections]
  );

  const toggleAllSections = useCallback((collapse: boolean) => {
    const newState = {} as any;
    Object.keys(collapsedSections).forEach(key => { newState[key] = collapse; });
    setCollapsedSections(newState);
  }, [collapsedSections]);

  useEffect(() => {
    if (existingData && !hasLoadedExistingData.current) {
      setFormData(prev => ({ ...prev, ...existingData, goals: initialGoals('OT', existingData) }));
      if (existingData.formConfig) {
        setFormConfig(existingData.formConfig);
      }
      hasLoadedExistingData.current = true;
    }
  }, [existingData]);

  // Phase 19 F26: a plan with a certification period stores its dates in the cert period keys
  useEffect(() => {
    if (hasFixedCertPeriod(certificationPeriod)) setFormData(prev => applyCertPeriod(prev, certificationPeriod));
  }, [certificationPeriod]);

  const handleConfigChange = (key: string, value: boolean | string) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  const loadAssessments = async () => {
    if (assessmentsList.length > 0) return;
    
    try {
      setLoadingAssessments(true);
      const result = await AssessmentsService.getAssessments(patientId);
      // Filter based on discipline: OT
      const disciplineAssessments = result.assessments.filter(
        assessment => assessment.discipline === AssessmentDiscipline.OT
      );
      setAssessmentsList(disciplineAssessments);
    } catch (error) {
      console.error('Error loading assessments:', error);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const formatAssessmentType = (assessmentType: string | undefined): string => {
    if (!assessmentType) return 'N/A';
    
    const typeMap: Record<string, string> = {
      'START_OF_CARE': 'Start of Care',
      'INITIAL': 'Start of Care',
      'initial': 'Start of Care',
      'REASSESSMENT': 'Recertification',
      'REASSESSMEMT': 'Recertification',
      'recertification': 'Recertification',
      'END_OF_CARE': 'Discharge',
      'discharge': 'Discharge'
    };
    
    return typeMap[assessmentType] || assessmentType;
  };

  const handleAssessmentSelection = (assessmentId: string) => {
    const selectedAssessment = assessmentsList.find(a => a.assessmentId === assessmentId);
    
    if (selectedAssessment) {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: selectedAssessment.date ? new Date(selectedAssessment.date).toISOString().split('T')[0] : '',
        assessedBy: selectedAssessment.providerName || '',
        assessmentType: selectedAssessment.assessmentType || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: '',
        assessedBy: '',
        assessmentType: ''
      }));
    }
  };

  // getStatusColor / getStatusDisplayText removed — use <DocStatusPill /> from common/Pills

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    Object.assign(newErrors, goalErrors(formData.goals));
    
    // CMS-485 Required fields
    if (!formData.certificationPeriodFrom) newErrors.certificationPeriodFrom = 'Certification start date is required';
    if (!formData.certificationPeriodTo) newErrors.certificationPeriodTo = 'Certification end date is required';
    if (!formData.primaryDiagnosis.trim()) newErrors.primaryDiagnosis = 'Primary diagnosis is required';
    if (formData.orderingPhysicianNPI && !/^\d{10}$/.test(formData.orderingPhysicianNPI)) newErrors.orderingPhysicianNPI = 'NPI must be 10 digits';
    if (!formData.prognosis) newErrors.prognosis = 'Prognosis is required';
    if (!formData.rehabilitationPotential) newErrors.rehabilitationPotential = 'Rehabilitation potential is required';
    
    
    // At least one intervention required
    if (!formData.interventionTherapeuticActivity.trim() && !formData.interventionFineMotorTraining.trim()) {
      newErrors.interventionTherapeuticActivity = 'At least one OT intervention is required';
    }
    
    // Frequency and duration required for billing
    if (!formData.otFrequency) newErrors.otFrequency = 'Visit frequency is required';
    if (!formData.otDuration) newErrors.otDuration = 'Treatment duration is required';
    if (!formData.providerName.trim()) newErrors.providerName = 'Provider name is required';
    
    // Home health specific
    if (formConfig.settingType === 'home-health') {
      if (!formData.homeboundStatus) newErrors.homeboundStatus = 'Homebound status is required for home health';
    }
    
    // Pediatric PDN specific
    if (formConfig.patientType === 'pediatric' && formConfig.isPDN) {
      if (!formData.pdnMedicalStabilityForOT) newErrors.pdnMedicalStabilityForOT = 'Medical stability for OT is required';
      if (!formData.otFrequencyJustification.trim()) newErrors.otFrequencyJustification = 'Frequency justification is required for Medicaid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (submitAfterSave = false) => {
    // Phase 4: Save keeps a DRAFT without required-field checks; Submit validates first
    if (!submitAfterSave || validateForm()) {
      onSave({ ...formData, ...clearedLegacyGoalFields('OT'), goals: formData.goals, formConfig, status: 'Draft' }, submitAfterSave);
    }
  };

  const handleChange = useCallback((field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: '' };
    });
  }, []);

  const isPediatric = formConfig.patientType === 'pediatric';
  const isPediatricPDN = formConfig.patientType === 'pediatric' && formConfig.isPDN;
  const isHomeHealth = formConfig.settingType === 'home-health';

  const isAnyLoading = saving || submitting || reopening || startingQA;
  const isDraft = !carePlanStatus || carePlanStatus === CarePlanStatus.DRAFT;
  const isSubmittedOrQA = carePlanStatus === CarePlanStatus.SUBMITTED || carePlanStatus === CarePlanStatus.QA;

  // Phase 4 (ASM-03): dirty tracking, autosave while DRAFT, unsaved-changes guard
  const draft = useCarePlanDraft({
    getSnapshot: () => JSON.stringify({ formData, formConfig }),
    getSaveData: () => ({ ...formData, ...clearedLegacyGoalFields('OT'), goals: formData.goals, formConfig, status: 'Draft' }),
    onAutosave,
    enabled: autosaveEnabled && isDraft && !isReadOnly && !!editingId,
    resetKey: `${editingId ?? 'new'}:${carePlanStatus}`,
  });
  const handleClose = () => draft.confirmLeave(onCancel);
  const isComplete = carePlanStatus === CarePlanStatus.COMPLETE;

  const actions: ActionButtonConfig[] = [
    { key: 'cancel', label: 'Close', onClick: handleClose, variant: 'secondary', disabled: isAnyLoading },
    { key: 'save', label: 'Save', loadingLabel: editingId ? 'Updating...' : 'Saving...', onClick: () => handleSave(false), variant: 'primary', disabled: isAnyLoading, loading: saving, hidden: !isDraft || isReadOnly },
    { key: 'submit', label: 'Submit', loadingLabel: 'Submitting...', onClick: () => handleSave(true), variant: 'primary', disabled: isAnyLoading, loading: submitting, hidden: !isDraft || isReadOnly },
    { key: 'reject', label: 'Reject', loadingLabel: 'Rejecting...', onClick: onReopen ?? (() => {}), variant: 'danger', disabled: isAnyLoading, loading: reopening, hidden: !isSubmittedOrQA || !onReopen || isReadOnly },
    { key: 'startQA', label: 'Start QA', loadingLabel: 'Starting QA...', onClick: onStartQA ?? (() => {}), variant: 'warning', disabled: isAnyLoading, loading: startingQA, hidden: carePlanStatus !== CarePlanStatus.SUBMITTED || !onStartQA || isReadOnly },
    { key: 'approve', label: 'Approve', loadingLabel: 'Approving...', onClick: onApprove ?? (() => {}), variant: 'success', disabled: isAnyLoading, loading: submitting, hidden: !isSubmittedOrQA || !onApprove || isReadOnly },
    { key: 'reopen', label: 'Reopen', loadingLabel: 'Reopening...', onClick: onReopen ?? (() => {}), variant: 'primary', disabled: isAnyLoading, loading: reopening, hidden: !isComplete || !onReopen || isReadOnly, className: 'bg-blue-600 hover:bg-blue-700' },
  ];

  return (
    <FormPageLayout
      onBack={handleClose}
      actions={actions}
      title="Occupational Therapy Care Plan"
      headerRight={
        <div className="flex items-center gap-2">
          {draft.indicator}
          {headerExtra}
          <DocStatusPill status={carePlanStatus} />
          <button
            type="button"
            onClick={() => toggleAllSections(!allSectionsCollapsed)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md border border-slate-200 transition-colors"
          >
            {allSectionsCollapsed ? (
              <><ChevronsDown size={14} /><span>Expand All</span></>
            ) : (
              <><ChevronsUp size={14} /><span>Collapse All</span></>
            )}
          </button>
        </div>
      }
    >
      <div {...draft.captureProps}>
          <div
            className="p-4 cursor-pointer flex justify-between items-center"
            onClick={() => setCollapsedSections(prev => ({ ...prev, config: !prev.config }))}
          >
            <h4 className="text-sm font-medium text-gray-700 flex items-center">
              <ClipboardList size={16} className="mr-2" />
              Care Plan Configuration
            </h4>
            {collapsedSections.config ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </div>
          <div className={collapsedSections.config ? 'hidden' : 'p-4 border-t border-gray-300'}>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Care Plan Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Patient Type</label>
              <select disabled={isReadOnly} className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                value={formConfig.patientType}
                onChange={(e) => handleConfigChange('patientType', e.target.value)}>
                <option value="pediatric">Pediatric</option>
                <option value="adult">Adult</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Setting</label>
              <select disabled={isReadOnly} className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                value={formConfig.settingType}
                onChange={(e) => handleConfigChange('settingType', e.target.value)}>
                <option value="home-health">Home Health</option>
                <option value="outpatient">Outpatient</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center text-sm text-gray-600">
                <input type="checkbox" disabled={isReadOnly} className={`mr-2 rounded border-gray-300 text-purple-600 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                  checked={formConfig.isPDN}
                  onChange={(e) => handleConfigChange('isPDN', e.target.checked)} />
                PDN Patient (Private Duty Nursing)
              </label>
            </div>
          </div>
          {isPediatricPDN && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start">
              <Hand size={18} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <strong>Pediatric PDN OT Care Plan:</strong> Includes fine motor development, sensory processing, feeding therapy, and caregiver training sections required for Texas Medicaid PDN authorization. Modifier UA may apply for specialized PDN services.
              </div>
            </div>
          )}
          
          {/* Assessment Reference */}
          <div className="mt-6">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Assessment Reference</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Occupational Therapy Assessment *</label>
                <select
                  ref={selectRef}
                  value={formData.assessmentId}
                  onChange={(e) => handleAssessmentSelection(e.target.value)}
                  onFocus={loadAssessments}
                  className={`w-full px-2 py-2 text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.assessmentId ? 'border-red-500' : 'border-gray-300'} max-w-full ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  disabled={loadingAssessments || isReadOnly}
                >
                  <option value="">{loadingAssessments ? 'Loading OT assessments...' : 'Select an OT assessment'}</option>
                  {assessmentsList.map((assessment) => (
                    <option key={assessment.assessmentId} value={assessment.assessmentId}>
                      {formatAssessmentType(assessment.assessmentType)} | {new Date(assessment.date).toLocaleDateString()} | {assessment.providerName}
                    </option>
                  ))}
                </select>
                {errors.assessmentId && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.assessmentId}</p>}
              </div>
              
              {/* Selected Assessment Details */}
              {formData.assessmentId && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h6 className="text-sm font-semibold text-purple-900 mb-2">Selected Assessment Details</h6>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Assessment ID:</span>
                      <p className="text-gray-900 mt-0.5">{formData.assessmentId}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Type:</span>
                      <p className="text-gray-900 mt-0.5">
                        {formatAssessmentType(formData.assessmentType)}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Assessment Date:</span>
                      <p className="text-gray-900 mt-0.5">
                        {formData.assessmentDate ? new Date(formData.assessmentDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Assessed By:</span>
                      <p className="text-gray-900 mt-0.5">{formData.assessedBy || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-8 text-left">
          {/* ========== CERTIFICATION PERIOD ========== */}
          <CertPeriodSection fixedPeriod={certificationPeriod}
            variant="card"
            title="Certification Period (CMS-485 / Texas Medicaid)"
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections.certification}
            onToggle={() => setCollapsedSections(prev => ({ ...prev, certification: !prev.certification }))}
          />

          {/* ========== ORDERING PHYSICIAN (Phase 19 F3) ========== */}
          <OrderingPhysicianSection
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections.orderingPhysician}
            onToggle={() => setCollapsedSections(prev => ({ ...prev, orderingPhysician: !prev.orderingPhysician }))}
          />

          {/* ========== PRIMARY DIAGNOSIS (Phase 19 F3: required, was never rendered) ========== */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label htmlFor="ot-primary-diagnosis" className="block text-sm font-medium text-gray-700 mb-1">Primary diagnosis (ICD-10) *</label>
            <ICDSelect
              id="ot-primary-diagnosis"
              value={formData.primaryDiagnosis}
              onChange={(value) => {
                handleChange('primaryDiagnosis', value);
                handleChange('primaryDiagnosisICD10', value.split(' - ')[0] || '');
              }}
              placeholder="Search ICD-10 code or description"
              disabled={isReadOnly}
              invalid={Boolean(errors.primaryDiagnosis)}
            />
            {errors.primaryDiagnosis && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.primaryDiagnosis}</p>}
          </div>

          {/* ========== CLINICAL STATUS ========== */}
          <div className="bg-orange-50 rounded-lg border border-orange-200">
            <div 
              className="p-4 cursor-pointer flex justify-between items-center"
              onClick={() => setCollapsedSections(prev => ({ ...prev, clinicalStatus: !prev.clinicalStatus }))}
            >
              <h4 className="text-md font-medium text-orange-900 flex items-center">
                <ClipboardList size={18} className="mr-2" />
                Clinical Status
              </h4>
              {collapsedSections.clinicalStatus ? <ChevronDown size={18} className="text-orange-900" /> : <ChevronUp size={18} className="text-orange-900" />}
            </div>
            <div className={collapsedSections.clinicalStatus ? 'hidden' : 'p-4 border-t border-orange-300'}>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prognosis *</label>
                <select disabled={isReadOnly} value={formData.prognosis} onChange={(e) => handleChange('prognosis', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.prognosis ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select prognosis</option>
                  <option value="Poor">Poor</option>
                  <option value="Guarded">Guarded</option>
                  <option value="Fair">Fair</option>
                  <option value="Good">Good</option>
                  <option value="Excellent">Excellent</option>
                </select>
                {errors.prognosis && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.prognosis}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rehabilitation Potential *</label>
                <select disabled={isReadOnly} value={formData.rehabilitationPotential} onChange={(e) => handleChange('rehabilitationPotential', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.rehabilitationPotential ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select potential</option>
                  <option value="None">None</option>
                  <option value="Minimal">Minimal</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Good">Good</option>
                  <option value="Excellent">Excellent</option>
                </select>
                {errors.rehabilitationPotential && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.rehabilitationPotential}</p>}
              </div>
            </div>
            </div>
          </div>

          {/* ========== HOMEBOUND STATUS (Home Health) ========== */}
          {isHomeHealth && (
            <HomeboundSection
              title="Homebound Status (Home Health Requirement)"
              tone="indigo"
              icon={Shield}
              columns={2}
              fields={otHomeboundFields(isPediatric)}
              values={formData}
              onChange={handleChange}
              errors={errors}
              readOnly={isReadOnly}
              collapsed={collapsedSections.homebound}
              onToggle={() => setCollapsedSections(prev => ({ ...prev, homebound: !prev.homebound }))}
            />
          )}

          {/* ========== FUNCTIONAL LIMITATIONS (CMS-485) ========== */}
          <div className="bg-cyan-50 rounded-lg border border-cyan-200">
            <div 
              className="p-4 cursor-pointer flex justify-between items-center"
              onClick={() => setCollapsedSections(prev => ({ ...prev, functionalLimitations: !prev.functionalLimitations }))}
            >
              <h4 className="text-md font-medium text-cyan-900">Functional Limitations (CMS-485)</h4>
              {collapsedSections.functionalLimitations ? <ChevronDown size={18} className="text-cyan-900" /> : <ChevronUp size={18} className="text-cyan-900" />}
            </div>
            <div className={collapsedSections.functionalLimitations ? 'hidden' : 'p-4 border-t border-cyan-300'}>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <CheckboxField field="functionalLimitationSelfCare" label="Self-Care/ADLs" checked={formData.functionalLimitationSelfCare} onChange={handleChange} />
              <CheckboxField field="functionalLimitationUpperExtremity" label="Upper Extremity" checked={formData.functionalLimitationUpperExtremity} onChange={handleChange}  isReadOnly={isReadOnly} />
              <CheckboxField field="functionalLimitationFineMotor" label="Fine Motor" checked={formData.functionalLimitationFineMotor} onChange={handleChange}  isReadOnly={isReadOnly} />
              <CheckboxField field="functionalLimitationSensoryProcessing" label="Sensory Processing" checked={formData.functionalLimitationSensoryProcessing} onChange={handleChange}  isReadOnly={isReadOnly} />
              <CheckboxField field="functionalLimitationCognitive" label="Cognitive" checked={formData.functionalLimitationCognitive} onChange={handleChange}  isReadOnly={isReadOnly} />
              <CheckboxField field="functionalLimitationVisualPerceptual" label="Visual-Perceptual" checked={formData.functionalLimitationVisualPerceptual} onChange={handleChange}  isReadOnly={isReadOnly} />
              <CheckboxField field="functionalLimitationFeeding" label="Feeding/Eating" checked={formData.functionalLimitationFeeding} onChange={handleChange} />
              <CheckboxField field="functionalLimitationWriting" label="Writing/Handwriting" checked={formData.functionalLimitationWriting} onChange={handleChange} />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Other Functional Limitations</label>
              <input type="text" disabled={isReadOnly} value={formData.functionalLimitationOther}
                onChange={(e) => handleChange('functionalLimitationOther', e.target.value)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                placeholder="Describe other limitations" />
            </div>
            </div>
          </div>

          {/* ========== PEDIATRIC DEVELOPMENTAL GOALS ========== */}
          {isPediatric && (
            <div className="bg-violet-50 rounded-lg border border-violet-200">
              <div 
                className="p-4 cursor-pointer flex justify-between items-center"
                onClick={() => setCollapsedSections(prev => ({ ...prev, pediatricDev: !prev.pediatricDev }))}
              >
                <h4 className="text-md font-medium text-violet-900 flex items-center">
                  <Baby size={18} className="mr-2" />
                  Pediatric Developmental Fine Motor Goals
                </h4>
                {collapsedSections.pediatricDev ? <ChevronDown size={18} className="text-violet-900" /> : <ChevronUp size={18} className="text-violet-900" />}
              </div>
              <div className={collapsedSections.pediatricDev ? 'hidden' : 'p-4 border-t border-violet-300'}>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chronological Age</label>
                  <input type="text" value={formData.pediatricChronologicalAge}
                    onChange={(e) => handleChange('pediatricChronologicalAge', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., 2 years 4 months" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adjusted Age (if applicable)</label>
                  <input type="text" value={formData.pediatricAdjustedAge}
                    onChange={(e) => handleChange('pediatricAdjustedAge', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., 1 year 10 months" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fine Motor Age Equivalent</label>
                  <input type="text" value={formData.pediatricFineMotorAge}
                    onChange={(e) => handleChange('pediatricFineMotorAge', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., 9-12 months" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Milestones</label>
                  <input type="text" value={formData.pediatricDevelopmentalMilestones}
                    onChange={(e) => handleChange('pediatricDevelopmentalMilestones', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., Emerging grasp, transfers objects" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              </div>
            </div>
            </div>
          )}

          {/* ========== PEDIATRIC PDN - OT CONSIDERATIONS ========== */}
          {isPediatricPDN && (
            <div className="bg-red-50 rounded-lg border border-red-200">
              <div 
                className="p-4 cursor-pointer flex justify-between items-center"
                onClick={() => setCollapsedSections(prev => ({ ...prev, pdnOT: !prev.pdnOT }))}
              >
                <h4 className="text-md font-medium text-red-900 flex items-center">
                  <Heart size={18} className="mr-2" />
                  PDN OT Considerations (Texas Medicaid)
                </h4>
                {collapsedSections.pdnOT ? <ChevronDown size={18} className="text-red-900" /> : <ChevronUp size={18} className="text-red-900" />}
              </div>
              <div className={collapsedSections.pdnOT ? 'hidden' : 'p-4 border-t border-red-300'}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tracheostomy Status</label>
                  <select disabled={isReadOnly} value={formData.pdnTracheostomyStatus}
                    onChange={(e) => handleChange('pdnTracheostomyStatus', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select status</option>
                    <option value="No tracheostomy">No tracheostomy</option>
                    <option value="Tracheostomy present">Tracheostomy present</option>
                    <option value="Tracheostomy - speaking valve use">Tracheostomy - uses speaking valve</option>
                    <option value="Recently decannulated">Recently decannulated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ventilator Status</label>
                  <select disabled={isReadOnly} value={formData.pdnVentilatorStatus}
                    onChange={(e) => handleChange('pdnVentilatorStatus', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select status</option>
                    <option value="No ventilator">No ventilator</option>
                    <option value="Ventilator dependent - continuous">Ventilator dependent - continuous</option>
                    <option value="Ventilator dependent - nocturnal only">Ventilator dependent - nocturnal only</option>
                    <option value="Weaning in progress">Weaning in progress</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medical Stability for OT *</label>
                  <select value={formData.pdnMedicalStabilityForOT}
                    onChange={(e) => handleChange('pdnMedicalStabilityForOT', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.pdnMedicalStabilityForOT ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select status</option>
                    <option value="Medically stable - full OT activities">Medically stable - full OT activities</option>
                    <option value="Stable with precautions">Stable with precautions - modified activities</option>
                    <option value="Medically fragile - bedside OT only">Medically fragile - bedside OT only</option>
                    <option value="Unstable - OT on hold">Unstable - OT on hold</option>
                  </select>
                  {errors.pdnMedicalStabilityForOT && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.pdnMedicalStabilityForOT}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feeding Status</label>
                  <select disabled={isReadOnly} value={formData.pdnFeedingStatus}
                    onChange={(e) => handleChange('pdnFeedingStatus', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select status</option>
                    <option value="Oral feeding only">Oral feeding only</option>
                    <option value="G-tube with oral trials">G-tube with oral trials</option>
                    <option value="G-tube only - no oral">G-tube only - no oral</option>
                    <option value="NPO - aspiration risk">NPO - aspiration risk</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">OT Session Precautions</label>
                  <textarea disabled={isReadOnly} value={formData.pdnOTSessionPrecautions}
                    onChange={(e) => handleChange('pdnOTSessionPrecautions', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., Position upright for respiratory support, monitor SpO2, seizure precautions" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Positioning for OT</label>
                  <textarea disabled={isReadOnly} value={formData.pdnPositioningForOT}
                    onChange={(e) => handleChange('pdnPositioningForOT', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., Supported sitting in Tumble Forms, prone over wedge" />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ========== PEDIATRIC PDN - SENSORY PROCESSING ========== */}
          {isPediatricPDN && (
            <div className="bg-fuchsia-50 rounded-lg border border-fuchsia-200">
              <div 
                className="p-4 cursor-pointer flex justify-between items-center"
                onClick={() => setCollapsedSections(prev => ({ ...prev, pdnSensory: !prev.pdnSensory }))}
              >
                <h4 className="text-md font-medium text-fuchsia-900 flex items-center">
                  <Brain size={18} className="mr-2" />
                  Sensory Processing Plan
                </h4>
                {collapsedSections.pdnSensory ? <ChevronDown size={18} className="text-fuchsia-900" /> : <ChevronUp size={18} className="text-fuchsia-900" />}
              </div>
              <div className={collapsedSections.pdnSensory ? 'hidden' : 'p-4 border-t border-fuchsia-300'}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overall Sensory Profile</label>
                  <select value={formData.pdnSensoryProfile}
                    onChange={(e) => handleChange('pdnSensoryProfile', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select profile</option>
                    <option value="Typical sensory processing">Typical sensory processing</option>
                    <option value="Sensory seeking">Sensory seeking</option>
                    <option value="Sensory avoiding">Sensory avoiding/over-responsive</option>
                    <option value="Under-responsive">Under-responsive/low registration</option>
                    <option value="Mixed pattern">Mixed pattern</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tactile Processing</label>
                  <textarea value={formData.pdnTactileProcessing}
                    onChange={(e) => handleChange('pdnTactileProcessing', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., Tactile defensive to light touch, tolerates deep pressure" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vestibular Processing</label>
                  <textarea value={formData.pdnVestibularProcessing}
                    onChange={(e) => handleChange('pdnVestibularProcessing', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., Gravitational insecurity, seeks movement" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Oral Sensory Processing</label>
                  <textarea value={formData.pdnOralSensoryProcessing}
                    onChange={(e) => handleChange('pdnOralSensoryProcessing', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., Oral defensiveness, limited diet, mouths objects" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sensory Diet Plan</label>
                  <textarea value={formData.pdnSensoryDietPlan}
                    onChange={(e) => handleChange('pdnSensoryDietPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Specific sensory activities for caregiver implementation" />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ========== PEDIATRIC PDN - FEEDING/ORAL MOTOR ========== */}
          {isPediatricPDN && (
            <div className="bg-lime-50 rounded-lg border border-lime-200">
              <div 
                className="p-4 cursor-pointer flex justify-between items-center"
                onClick={() => setCollapsedSections(prev => ({ ...prev, pdnFeeding: !prev.pdnFeeding }))}
              >
                <h4 className="text-md font-medium text-lime-900 flex items-center">
                  <Utensils size={18} className="mr-2" />
                  Feeding/Oral Motor OT Plan
                </h4>
                {collapsedSections.pdnFeeding ? <ChevronDown size={18} className="text-lime-900" /> : <ChevronUp size={18} className="text-lime-900" />}
              </div>
              <div className={collapsedSections.pdnFeeding ? 'hidden' : 'p-4 border-t border-lime-300'}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Feeding Method</label>
                  <select value={formData.pdnCurrentFeedingMethod}
                    onChange={(e) => handleChange('pdnCurrentFeedingMethod', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select method</option>
                    <option value="Oral only">Oral only</option>
                    <option value="G-tube bolus">G-tube bolus</option>
                    <option value="G-tube continuous">G-tube continuous</option>
                    <option value="Combination oral and tube">Combination oral and tube</option>
                    <option value="NPO">NPO</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Oral Feeding Status</label>
                  <select value={formData.pdnOralFeedingStatus}
                    onChange={(e) => handleChange('pdnOralFeedingStatus', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select status</option>
                    <option value="Full oral feeding">Full oral feeding - age appropriate diet</option>
                    <option value="Modified oral diet">Modified oral diet - texture restrictions</option>
                    <option value="Limited oral trials">Limited oral trials - therapeutic only</option>
                    <option value="NPO - aspiration risk">NPO - aspiration risk</option>
                    <option value="Oral stimulation only">Oral stimulation only - non-nutritive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Oral Trials Plan (if applicable)</label>
                  <textarea value={formData.pdnOralTrialsPlan}
                    onChange={(e) => handleChange('pdnOralTrialsPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Oral trial protocol, textures, amounts, frequency" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feeding Positioning</label>
                  <textarea value={formData.pdnFeedingPositioning}
                    onChange={(e) => handleChange('pdnFeedingPositioning', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., 90-90-90 in adapted high chair, chin tuck for swallowing" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Self-Feeding Status</label>
                  <select value={formData.pdnSelfFeedingStatus}
                    onChange={(e) => handleChange('pdnSelfFeedingStatus', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select status</option>
                    <option value="Independent self-feeding">Independent self-feeding</option>
                    <option value="Self-feeds with adapted equipment">Self-feeds with adapted equipment</option>
                    <option value="Assisted self-feeding">Assisted self-feeding - hand over hand</option>
                    <option value="Dependent">Dependent - fed by caregiver</option>
                    <option value="N/A - tube fed only">N/A - tube fed only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adapted Feeding Equipment</label>
                  <textarea value={formData.pdnAdaptedFeedingEquipment}
                    onChange={(e) => handleChange('pdnAdaptedFeedingEquipment', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., Nosey cup, built-up spoon, scoop dish, lip block" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feeding OT Interventions</label>
                  <textarea value={formData.pdnFeedingOTIntervention}
                    onChange={(e) => handleChange('pdnFeedingOTIntervention', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Specific feeding therapy techniques and progression" />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ========== PEDIATRIC PDN - CAREGIVER OT TRAINING ========== */}
          {isPediatricPDN && (
            <div className="bg-emerald-50 rounded-lg border border-emerald-200">
              <div 
                className="p-4 cursor-pointer flex justify-between items-center"
                onClick={() => setCollapsedSections(prev => ({ ...prev, pdnCaregiver: !prev.pdnCaregiver }))}
              >
                <h4 className="text-md font-medium text-emerald-900 flex items-center">
                  <User size={18} className="mr-2" />
                  Caregiver OT Training Plan
                </h4>
                {collapsedSections.pdnCaregiver ? <ChevronDown size={18} className="text-emerald-900" /> : <ChevronUp size={18} className="text-emerald-900" />}
              </div>
              <div className={collapsedSections.pdnCaregiver ? 'hidden' : 'p-4 border-t border-emerald-300'}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Caregiver for OT Training</label>
                  <input type="text" value={formData.pdnOTCaregiverName}
                    onChange={(e) => handleChange('pdnOTCaregiverName', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="Name of caregiver to train" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">OT Training Status</label>
                  <select value={formData.pdnOTCaregiverTrainingStatus}
                    onChange={(e) => handleChange('pdnOTCaregiverTrainingStatus', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select status</option>
                    <option value="Fully trained">Fully trained - all OT competencies verified</option>
                    <option value="Partially trained">Partially trained - some competencies pending</option>
                    <option value="Training in progress">Training in progress</option>
                    <option value="Not trained">Not trained - training scheduled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verified OT Competencies</label>
                  <textarea value={formData.pdnOTCaregiverCompetencies}
                    onChange={(e) => handleChange('pdnOTCaregiverCompetencies', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., ROM exercises, sensory diet, feeding techniques, splint care, ADL assistance" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">OT Training Plan</label>
                  <textarea value={formData.pdnOTCaregiverTrainingPlan}
                    onChange={(e) => handleChange('pdnOTCaregiverTrainingPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Training topics and schedule for caregiver OT education" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sensory Diet Training</label>
                  <textarea value={formData.pdnSensoryDietTraining}
                    onChange={(e) => handleChange('pdnSensoryDietTraining', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Training caregiver on implementing sensory diet throughout the day" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feeding Technique Training</label>
                  <textarea value={formData.pdnFeedingTechniqueTraining}
                    onChange={(e) => handleChange('pdnFeedingTechniqueTraining', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Training on oral motor techniques, positioning, pacing, adapted equipment" />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ========== GOALS (Phase 19 F12: one structure for every discipline) ========== */}
          <GoalsEditor
            discipline="OT"
            goals={formData.goals}
            onChange={(goals) => setFormData(prev => ({ ...prev, goals }))}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={Boolean(collapsedSections.goals)}
            onToggle={() => setCollapsedSections(prev => ({ ...prev, goals: !prev.goals }))}
          />

          {/* ========== OT INTERVENTIONS ========== */}
          <div className="bg-amber-50 rounded-lg border border-amber-200">
            <div 
              className="p-4 cursor-pointer flex justify-between items-center"
              onClick={() => setCollapsedSections(prev => ({ ...prev, interventions: !prev.interventions }))}
            >
              <h4 className="text-md font-medium text-amber-900">Occupational Therapy Interventions</h4>
              {collapsedSections.interventions ? <ChevronDown size={18} className="text-amber-900" /> : <ChevronUp size={18} className="text-amber-900" />}
            </div>
            <div className={collapsedSections.interventions ? 'hidden' : 'p-4 border-t border-amber-300'}>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Therapeutic Activity *</label>
                <textarea value={formData.interventionTherapeuticActivity}
                  onChange={(e) => handleChange('interventionTherapeuticActivity', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.interventionTherapeuticActivity ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  rows={3} placeholder={isPediatric 
                    ? "e.g., Developmental play activities, fine motor manipulation, sensory exploration"
                    : "e.g., Functional task practice, activity modification, energy conservation"} />
                {errors.interventionTherapeuticActivity && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.interventionTherapeuticActivity}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sensory Integration</label>
                <textarea value={formData.interventionSensoryIntegration}
                  onChange={(e) => handleChange('interventionSensoryIntegration', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="e.g., Sensory diet activities, sensory modulation strategies, self-regulation training" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fine Motor Training</label>
                <textarea value={formData.interventionFineMotorTraining}
                  onChange={(e) => handleChange('interventionFineMotorTraining', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder={isPediatric 
                    ? "e.g., Grasp development, in-hand manipulation, bilateral coordination"
                    : "e.g., Grip strengthening, dexterity training, coordination exercises"} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Feeding Therapy</label>
                <textarea value={formData.interventionFeedingTherapy}
                  onChange={(e) => handleChange('interventionFeedingTherapy', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="e.g., Oral motor exercises, food exploration, self-feeding training" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ADL Training</label>
                <textarea value={formData.interventionADLTraining}
                  onChange={(e) => handleChange('interventionADLTraining', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder={isPediatric 
                    ? "e.g., Dressing skills, grooming, toileting readiness"
                    : "e.g., Dressing, bathing, grooming, toileting adaptations"} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caregiver Training</label>
                <textarea value={formData.interventionCaregiverTraining}
                  onChange={(e) => handleChange('interventionCaregiverTraining', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="e.g., HEP instruction, sensory diet implementation, feeding technique training" />
              </div>
            </div>
            </div>
          </div>

          {/* ========== OT VISIT FREQUENCY ========== */}
          <div className="bg-purple-50 rounded-lg border border-purple-200">
            <div 
              className="p-4 cursor-pointer flex justify-between items-center"
              onClick={() => setCollapsedSections(prev => ({ ...prev, frequency: !prev.frequency }))}
            >
              <h4 className="text-md font-medium text-purple-900 flex items-center">
                <Clock size={18} className="mr-2" />
                OT Visit Frequency and Duration
              </h4>
              {collapsedSections.frequency ? <ChevronDown size={18} className="text-purple-900" /> : <ChevronUp size={18} className="text-purple-900" />}
            </div>
            <div className={collapsedSections.frequency ? 'hidden' : 'p-4 border-t border-purple-300'}>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visit Frequency *</label>
                <select value={formData.otFrequency} onChange={(e) => handleChange('otFrequency', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.otFrequency ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select frequency</option>
                  <option value="1x/week">1x/week</option>
                  <option value="2x/week">2x/week</option>
                  <option value="3x/week">3x/week</option>
                  <option value="1x/month">1x/month</option>
                  <option value="2x/month">2x/month</option>
                  <option value="Custom">Custom - specify below</option>
                </select>
                {errors.otFrequency && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.otFrequency}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Frequency</label>
                <input type="text" value={formData.otFrequencyCustom}
                  onChange={(e) => handleChange('otFrequencyCustom', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  placeholder="e.g., 2x/week x 4 weeks, then 1x/week x 4 weeks" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                <select value={formData.otDuration} onChange={(e) => handleChange('otDuration', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.otDuration ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select duration</option>
                  <option value="30 days">30 days</option>
                  <option value="60 days">60 days</option>
                  <option value="90 days">90 days</option>
                  <option value="6 months">6 months</option>
                  <option value="1 year">1 year</option>
                  <option value="Ongoing">Ongoing (recertify as needed)</option>
                </select>
                {errors.otDuration && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.otDuration}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Session Length</label>
                <select disabled={isReadOnly} value={formData.otSessionLength} onChange={(e) => handleChange('otSessionLength', e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select session length</option>
                  <option value="15 minutes">15 minutes</option>
                  <option value="30 minutes">30 minutes</option>
                  <option value="45 minutes">45 minutes</option>
                  <option value="60 minutes">60 minutes</option>
                </select>
              </div>
              {isPediatricPDN && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequency Justification (Texas Medicaid) *</label>
                  <textarea value={formData.otFrequencyJustification}
                    onChange={(e) => handleChange('otFrequencyJustification', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.otFrequencyJustification ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder="Justify medical necessity for requested OT frequency. Include: fine motor delays, sensory processing needs, feeding concerns, caregiver training requirements" />
                  {errors.otFrequencyJustification && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.otFrequencyJustification}</p>}
                </div>
              )}
            </div>
            </div>
          </div>

          {/* ========== COORDINATION OF CARE ========== */}
          <div className="bg-teal-50 rounded-lg border border-teal-200">
            <div 
              className="p-4 cursor-pointer flex justify-between items-center"
              onClick={() => setCollapsedSections(prev => ({ ...prev, coordination: !prev.coordination }))}
            >
              <h4 className="text-md font-medium text-teal-900">Coordination of Care</h4>
              {collapsedSections.coordination ? <ChevronDown size={18} className="text-teal-900" /> : <ChevronUp size={18} className="text-teal-900" />}
            </div>
            <div className={collapsedSections.coordination ? 'hidden' : 'p-4 border-t border-teal-300'}>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coordination with Nursing</label>
                <textarea value={formData.coordinationWithNursing}
                  onChange={(e) => handleChange('coordinationWithNursing', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                  placeholder="OT activities for nursing carryover, positioning, feeding, sensory strategies" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coordination with PT</label>
                <textarea value={formData.coordinationWithPT}
                  onChange={(e) => handleChange('coordinationWithPT', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                  placeholder="Joint goals, positioning, equipment needs" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coordination with ST</label>
                <textarea value={formData.coordinationWithST}
                  onChange={(e) => handleChange('coordinationWithST', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                  placeholder="Feeding therapy coordination, oral motor, communication strategies" />
              </div>
              {isPediatric && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coordination with School/ECI</label>
                  <textarea value={formData.coordinationWithSchool}
                    onChange={(e) => handleChange('coordinationWithSchool', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="IEP coordination, ECI transition, school therapy alignment" />
                </div>
              )}
            </div>
            </div>
          </div>

          {/* ========== PROVIDER INFORMATION ========== */}
          <ProviderSection
            tone="pink"
            keys={['providerName', 'providerCredentials', 'providerNPI', 'supervisingPhysician', 'supervisingPhysicianNPI', 'dateCreated']}
            overrides={{ providerCredentials: { placeholder: 'e.g., OTR/L, OTD' } }}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections.provider}
            onToggle={() => setCollapsedSections(prev => ({ ...prev, provider: !prev.provider }))}
          />

          {/* ========== MEDICAL NECESSITY STATEMENT ========== */}
          <MedicalNecessitySection
            title="Medical Necessity Statement"
            tone="slate"
            icon={null}
            fields={OT_MEDICAL_NECESSITY_FIELDS}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections.medicalNecessity}
            onToggle={() => setCollapsedSections(prev => ({ ...prev, medicalNecessity: !prev.medicalNecessity }))}
          />

          {/* ========== ADDITIONAL NOTES ========== */}
          <div className="bg-yellow-50 rounded-lg border border-yellow-200">
            <div 
              className="p-4 cursor-pointer flex justify-between items-center"
              onClick={() => setCollapsedSections(prev => ({ ...prev, additionalNotes: !prev.additionalNotes }))}
            >
              <h4 className="text-md font-medium text-yellow-900">Additional Notes</h4>
              {collapsedSections.additionalNotes ? <ChevronDown size={18} className="text-yellow-900" /> : <ChevronUp size={18} className="text-yellow-900" />}
            </div>
            <div className={collapsedSections.additionalNotes ? 'hidden' : 'p-4 border-t border-yellow-300'}>

            <textarea value={formData.additionalNotes}
              onChange={(e) => handleChange('additionalNotes', e.target.value)}
              disabled={isReadOnly}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
              placeholder="Any additional notes or special considerations..." />
            </div>
          </div>

        </div>
      </div>
      {draft.dialog}
    </FormPageLayout>
  );
};

export default OTCarePlanForm;
